import { useState, useEffect, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { toErr } from '@easyflow/shared';
import {
  Plus, Mail, Trash2, Star, StarOff, TestTube, Loader2, CheckCircle2, CheckCircle,
  XCircle, AlertCircle, Edit, Shield, Server, Users, RefreshCw, Inbox, PenLine,
  ChevronDown, ChevronUp
} from 'lucide-react';
import { apiClient } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Card, CardContent } from '@easyflow/ui-frontend/components/ui/card';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-frontend/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@easyflow/ui-frontend/components/ui/dialog';
import { Switch } from '@easyflow/ui-frontend/components/ui/switch';
import RichTextEditor from '@easyflow/ui-frontend/components/ui/rich-text-editor';
import { getErrorMessage } from '@/lib/utils';


interface EmailAccount {
  id: string;
  account_type: 'gmail' | 'smtp';
  display_name: string;
  email_address: string;
  smtp_host?: string;
  smtp_port?: number;
  smtp_user?: string;
  smtp_security?: string;
  imap_host?: string;
  imap_port?: number;
  imap_user?: string;
  imap_security?: string;
  imap_protocol?: string;
  is_default: boolean;
  is_active: boolean;
  google_account_id?: string;
  last_test_at?: string;
  last_test_result?: string;
  last_test_error?: string;
  assignment_count?: number;
  created_at?: string;
  signature_html?: string | null;
  signature_enabled?: boolean;
  signature_on_reply?: boolean;
  gmail_shared?: boolean;
  gmail_owner_id?: string | null;
}

interface TenantUser {
  id: string;
  first_name?: string;
  last_name?: string;
  username?: string;
  email: string;
  role: string;
}

interface AccountFormData {
  display_name: string;
  email_address: string;
  smtp_host: string;
  smtp_port: number;
  smtp_user: string;
  smtp_password: string;
  smtp_security: string;
  imap_host: string;
  imap_port: number;
  imap_user: string;
  imap_password: string;
  imap_security: string;
  imap_protocol: string;
  is_default: boolean;
  signature_html: string;
  signature_enabled: boolean;
  signature_on_reply: boolean;
}

const defaultFormData: AccountFormData = {
  display_name: '',
  email_address: '',
  smtp_host: '',
  smtp_port: 587,
  smtp_user: '',
  smtp_password: '',
  smtp_security: 'tls',
  imap_host: '',
  imap_port: 993,
  imap_user: '',
  imap_password: '',
  imap_security: 'ssl',
  imap_protocol: 'imap',
  is_default: false,
  signature_html: '',
  signature_enabled: true,
  signature_on_reply: true,
};

export default function EmailAccounts() {
  const { toast } = useToast();
  const [accounts, setAccounts] = useState<EmailAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<AccountFormData>({ ...defaultFormData });
  const [saving, setSaving] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [testingImap, setTestingImap] = useState(false);
  const [smtpTestResult, setSmtpTestResult] = useState<{ success: boolean; error?: string } | null>(null);
  const [imapTestResult, setImapTestResult] = useState<{ success: boolean; error?: string } | null>(null);
  const [settingDefault, setSettingDefault] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [showAssignments, setShowAssignments] = useState(false);
  const [assignAccountId, setAssignAccountId] = useState<string | null>(null);
  const [tenantUsers, setTenantUsers] = useState<TenantUser[]>([]);
  const [assignedUserIds, setAssignedUserIds] = useState<string[]>([]);
  const [assignGlobal, setAssignGlobal] = useState<boolean>(false);
  const [savingAssignments, setSavingAssignments] = useState(false);
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [showGmailSig, setShowGmailSig] = useState(false);
  const [gmailSigAccount, setGmailSigAccount] = useState<EmailAccount | null>(null);
  const [gmailSigLoading, setGmailSigLoading] = useState(false);
  const [gmailSigSaving, setGmailSigSaving] = useState(false);
  const [gmailSigForm, setGmailSigForm] = useState<{ signature_html: string; signature_enabled: boolean; signature_on_reply: boolean }>({
    signature_html: '',
    signature_enabled: true,
    signature_on_reply: true,
  });
  const [expandedSignatures, setExpandedSignatures] = useState<Set<string>>(new Set());

  const hasSignatureContent = (html?: string | null): boolean => {
    if (!html) return false;
    if (/<(img|table|hr|svg)\b/i.test(html)) return true;
    return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim().length > 0;
  };

  const sanitizeSignatureHtml = (html?: string | null): string => {
    if (!html) return '';
    return DOMPurify.sanitize(html, {
      USE_PROFILES: { html: true },
      FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed'],
      FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover'],
    });
  };

  const getSignatureStatus = (account: EmailAccount): 'active' | 'disabled' | 'none' => {
    if (!hasSignatureContent(account.signature_html)) return 'none';
    return account.signature_enabled === false ? 'disabled' : 'active';
  };

  const toggleSignatureExpanded = (accountId: string) => {
    setExpandedSignatures((prev) => {
      const next = new Set(prev);
      if (next.has(accountId)) {
        next.delete(accountId);
      } else {
        next.add(accountId);
      }
      return next;
    });
  };

  const loadAccounts = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiClient.get<{ data?: EmailAccount[] }>('/email-accounts');
      if (res.success && Array.isArray(res.data?.data)) {
        setAccounts(res.data.data);
      } else if (res.success && Array.isArray(res.data)) {
        setAccounts(res.data);
      }
    } catch (err) {
      toast({ title: 'Errore', description: 'Impossibile caricare gli account email', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  // Task #902 — Upload signature image and return its embeddable URL
  const uploadSignatureImage = useCallback(async (file: File): Promise<string> => {
    const fd = new FormData();
    fd.append('image', file);
    const res = await apiClient.post<{ error?: string; data?: { url?: string } }>('/email-accounts/signature-images', fd);
    if (!res.success || !res.data?.data?.url) {
      throw new Error(res.error || res.data?.error || 'Caricamento immagine fallito');
    }
    return res.data.data.url as string;
  }, []);

  const handleImageUploadError = useCallback((message: string) => {
    toast({ title: 'Errore immagine', description: message, variant: 'destructive' });
  }, [toast]);

  const handleSave = async () => {
    if (!formData.email_address || !formData.smtp_host || !formData.smtp_user) {
      toast({ title: 'Errore', description: 'Compila i campi obbligatori SMTP', variant: 'destructive' });
      return;
    }
    if (!formData.smtp_password && !editingId) {
      toast({ title: 'Errore', description: 'Password SMTP obbligatoria', variant: 'destructive' });
      return;
    }

    try {
      setSaving(true);
      let res;
      if (editingId) {
        res = await apiClient.put(`/email-accounts/${editingId}`, formData);
      } else {
        res = await apiClient.post('/email-accounts', formData);
      }
      if (res.success) {
        toast({ title: 'Salvato', description: editingId ? 'Account aggiornato' : 'Account creato con successo' });
        setShowForm(false);
        setEditingId(null);
        setFormData({ ...defaultFormData });
        loadAccounts();
      } else {
        throw new Error(res.error || 'Errore salvataggio');
      }
    } catch (err) {
      toast({ title: 'Errore', description: getErrorMessage(err) || 'Errore salvataggio account', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleTestSmtp = async () => {
    if (!formData.smtp_host || !formData.smtp_user) {
      toast({ title: 'Errore', description: 'Compila host e utente SMTP', variant: 'destructive' });
      return;
    }
    try {
      setTestingSmtp(true);
      setSmtpTestResult(null);
      const payload: {
        host: string;
        port: number;
        user: string;
        security: string;
        password?: string;
        account_id?: string;
      } = {
        host: formData.smtp_host,
        port: formData.smtp_port,
        user: formData.smtp_user,
        security: formData.smtp_security,
      };
      if (formData.smtp_password) {
        payload.password = formData.smtp_password;
      } else if (editingId) {
        payload.account_id = editingId;
      }
      const res = await apiClient.post<{ success: boolean; error?: string; data?: { success: boolean; error?: string } }>('/email-accounts/test-smtp', payload);
      if (res.success) {
        setSmtpTestResult(res.data?.data || res.data || null);
      }
    } catch (err) {
      setSmtpTestResult({ success: false, error: getErrorMessage(err) });
    } finally {
      setTestingSmtp(false);
    }
  };

  const handleTestImap = async () => {
    if (!formData.imap_host || !formData.imap_user) {
      toast({ title: 'Errore', description: 'Compila host e utente IMAP', variant: 'destructive' });
      return;
    }
    try {
      setTestingImap(true);
      setImapTestResult(null);
      const payload: {
        host: string;
        port: number;
        user: string;
        security: string;
        password?: string;
        account_id?: string;
      } = {
        host: formData.imap_host,
        port: formData.imap_port,
        user: formData.imap_user,
        security: formData.imap_security,
      };
      if (formData.imap_password) {
        payload.password = formData.imap_password;
      } else if (editingId) {
        payload.account_id = editingId;
      }
      const res = await apiClient.post<{ success: boolean; error?: string; data?: { success: boolean; error?: string } }>('/email-accounts/test-imap', payload);
      if (res.success) {
        setImapTestResult(res.data?.data || res.data || null);
      }
    } catch (err) {
      setImapTestResult({ success: false, error: getErrorMessage(err) });
    } finally {
      setTestingImap(false);
    }
  };

  const handleSetDefault = async (id: string) => {
    try {
      setSettingDefault(id);
      const res = await apiClient.post(`/email-accounts/${id}/set-default`, {});
      if (res.success) {
        toast({ title: 'Predefinito', description: 'Account impostato come predefinito' });
        loadAccounts();
      }
    } catch (err) {
      toast({ title: 'Errore', description: getErrorMessage(err), variant: 'destructive' });
    } finally {
      setSettingDefault(null);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      setDeletingId(id);
      const res = await apiClient.delete(`/email-accounts/${id}`);
      if (res.success) {
        toast({ title: 'Eliminato', description: 'Account email eliminato' });
        loadAccounts();
      } else {
        throw new Error(res.error || 'Errore eliminazione');
      }
    } catch (err) {
      toast({ title: 'Errore', description: getErrorMessage(err) || ((err as { data?: unknown }).data as { error?: string } | undefined)?.error || 'Errore eliminazione', variant: 'destructive' });
    } finally {
      setDeletingId(null);
    }
  };

  const openEditForm = (account: EmailAccount) => {
    setEditingId(account.id);
    setFormData({
      display_name: account.display_name || '',
      email_address: account.email_address || '',
      smtp_host: account.smtp_host || '',
      smtp_port: account.smtp_port || 587,
      smtp_user: account.smtp_user || '',
      smtp_password: '',
      smtp_security: account.smtp_security || 'tls',
      imap_host: account.imap_host || '',
      imap_port: account.imap_port || 993,
      imap_user: account.imap_user || '',
      imap_password: '',
      imap_security: account.imap_security || 'ssl',
      imap_protocol: account.imap_protocol || 'imap',
      is_default: account.is_default,
      signature_html: account.signature_html || '',
      signature_enabled: account.signature_enabled !== false,
      signature_on_reply: account.signature_on_reply !== false,
    });
    setSmtpTestResult(null);
    setImapTestResult(null);
    setShowForm(true);
  };

  const openNewForm = () => {
    setEditingId(null);
    setFormData({ ...defaultFormData });
    setSmtpTestResult(null);
    setImapTestResult(null);
    setShowForm(true);
  };

  const [togglingShare, setTogglingShare] = useState<string | null>(null);
  const handleToggleGmailShared = async (account: EmailAccount) => {
    if (!account.google_account_id) return;
    const next = !account.gmail_shared;
    try {
      setTogglingShare(account.id);
      const res = await apiClient.put(
        `/integrations/google/${account.google_account_id}/gmail-sharing`,
        { gmail_shared: next }
      );
      if (res.success) {
        toast({
          title: next ? 'Condivisione attivata' : 'Condivisione disattivata',
          description: next
            ? 'Ora puoi assegnare l\'account ad altri utenti.'
            : 'Solo il proprietario può usare questo account Gmail.',
        });
        loadAccounts();
      } else {
        throw new Error(res.error || 'Operazione non riuscita');
      }
    } catch (err: unknown) {
      const __err = toErr(err);
      toast({ title: 'Errore', description: __err.message || 'Impossibile aggiornare la condivisione', variant: 'destructive' });
    } finally {
      setTogglingShare(null);
    }
  };

  const openAssignments = async (accountId: string) => {
    setAssignAccountId(accountId);
    setShowAssignments(true);
    try {
      const [usersRes, assignRes] = await Promise.all([
        apiClient.get<{ data?: TenantUser[] }>('/email-accounts/users/list'),
        apiClient.get<{ data?: Array<{ user_id: string }> } & Array<{ user_id: string }>>(`/email-accounts/${accountId}/assignments`),
      ]);
      const users = usersRes.data?.data || usersRes.data || [];
      setTenantUsers(Array.isArray(users) ? users : []);
      const assigned = assignRes.data?.data || assignRes.data || [];
      const assignedIds = Array.isArray(assigned) ? assigned.map((a: { user_id: string }) => a.user_id) : [];
      setAssignedUserIds(assignedIds);
      setAssignGlobal(assignedIds.length === 0);
    } catch (err) {
      toast({ title: 'Errore', description: 'Impossibile caricare utenti', variant: 'destructive' });
    }
  };

  const handleSaveAssignments = async () => {
    if (!assignAccountId) return;
    try {
      setSavingAssignments(true);
      const account = accounts.find((a) => a.id === assignAccountId);
      const ownerId = account?.account_type === 'gmail' ? account?.gmail_owner_id : undefined;
      let userIds: string[];
      if (assignGlobal) {
        userIds = [];
      } else {
        userIds = ownerId && !assignedUserIds.includes(ownerId)
          ? [...assignedUserIds, ownerId]
          : assignedUserIds;
      }
      const res = await apiClient.post(`/email-accounts/${assignAccountId}/assignments`, {
        user_ids: userIds,
      });
      if (res.success) {
        toast({ title: 'Salvato', description: 'Assegnazioni aggiornate' });
        setShowAssignments(false);
        loadAccounts();
      }
    } catch (err) {
      toast({ title: 'Errore', description: getErrorMessage(err), variant: 'destructive' });
    } finally {
      setSavingAssignments(false);
    }
  };

  const toggleUserAssignment = (userId: string) => {
    setAssignedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const handleSyncInbox = async (accountId: string) => {
    try {
      setSyncingId(accountId);
      const res = await apiClient.post<{ synced?: number; total?: number; data?: { synced?: number; total?: number } }>(`/email-accounts/${accountId}/sync`, { limit: 50 });
      if (res.success) {
        const data = res.data?.data || res.data;
        toast({
          title: 'Sincronizzazione completata',
          description: `${data?.synced || 0} nuove email sincronizzate su ${data?.total || 0} totali`,
        });
      }
    } catch (err) {
      toast({ title: 'Errore sincronizzazione', description: getErrorMessage(err), variant: 'destructive' });
    } finally {
      setSyncingId(null);
    }
  };

  const openGmailSignature = async (account: EmailAccount) => {
    if (!account.google_account_id) {
      toast({ title: 'Errore', description: 'Account Google non identificato', variant: 'destructive' });
      return;
    }
    setGmailSigAccount(account);
    setShowGmailSig(true);
    setGmailSigForm({
      signature_html: account.signature_html || '',
      signature_enabled: account.signature_enabled !== false,
      signature_on_reply: account.signature_on_reply !== false,
    });
    try {
      setGmailSigLoading(true);
      type GmailSignature = { signature_html?: string; signature_enabled?: boolean; signature_on_reply?: boolean };
      const res = await apiClient.get<{ data?: GmailSignature } & GmailSignature>(`/email-accounts/gmail/${account.google_account_id}/signature`);
      const data = res.data?.data || res.data;
      if (res.success && data) {
        setGmailSigForm({
          signature_html: data.signature_html || '',
          signature_enabled: data.signature_enabled !== false,
          signature_on_reply: data.signature_on_reply !== false,
        });
      }
    } catch (err) {
      toast({ title: 'Errore', description: 'Impossibile caricare la firma', variant: 'destructive' });
    } finally {
      setGmailSigLoading(false);
    }
  };

  const handleSaveGmailSignature = async () => {
    if (!gmailSigAccount?.google_account_id) return;
    try {
      setGmailSigSaving(true);
      const res = await apiClient.put(`/email-accounts/gmail/${gmailSigAccount.google_account_id}/signature`, gmailSigForm);
      if (res.success) {
        toast({ title: 'Salvato', description: 'Firma Gmail aggiornata' });
        setShowGmailSig(false);
        setGmailSigAccount(null);
        loadAccounts();
      } else {
        throw new Error(res.error || 'Errore salvataggio');
      }
    } catch (err) {
      toast({ title: 'Errore', description: getErrorMessage(err) || 'Errore salvataggio firma', variant: 'destructive' });
    } finally {
      setGmailSigSaving(false);
    }
  };

  const assignAccount = assignAccountId ? accounts.find((a) => a.id === assignAccountId) : null;

  return (
    <div className="p-6 w-full">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Account Email</h1>
          <p className="text-muted-foreground text-sm mt-1">Gestisci gli account email per l'invio e la ricezione (Gmail + SMTP personalizzato)</p>
        </div>
        <Button onClick={openNewForm} className="gap-2">
          <Plus className="w-4 h-4" />
          Aggiungi Account Email
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : accounts.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Mail className="w-12 h-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-lg mb-2">Nessun account email configurato</p>
            <p className="text-muted-foreground text-sm mb-4">Aggiungi un account SMTP personalizzato o collega un account Gmail dalle Integrazioni</p>
            <Button onClick={openNewForm} variant="outline" className="gap-2">
              <Plus className="w-4 h-4" />
              Aggiungi Account Email
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {accounts.map((account) => {
            const sigStatus = getSignatureStatus(account);
            const sigExpanded = expandedSignatures.has(account.id);
            const sigBadgeClasses =
              sigStatus === 'active'
                ? 'border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100'
                : sigStatus === 'disabled'
                ? 'border-amber-300 text-amber-700 bg-amber-50 hover:bg-amber-100'
                : 'border-gray-300 text-gray-500 bg-gray-50 hover:bg-gray-100';
            const sigLabel =
              sigStatus === 'active'
                ? 'Firma attiva'
                : sigStatus === 'disabled'
                ? 'Firma disattivata'
                : 'Nessuna firma';
            const canExpandSig = sigStatus !== 'none';
            return (
            <Card key={account.id} className={`border ${account.is_default ? 'border-blue-300 bg-blue-50/30' : ''}`}>
              <CardContent className="py-4 px-6">
                <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${account.account_type === 'gmail' ? 'bg-red-100' : 'bg-blue-100'}`}>
                    {account.account_type === 'gmail' ? (
                      <Mail className="w-5 h-5 text-red-600" />
                    ) : (
                      <Server className="w-5 h-5 text-blue-600" />
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-foreground">{account.display_name}</span>
                      <Badge variant={account.account_type === 'gmail' ? 'destructive' : 'default'} className="text-xs">
                        {account.account_type === 'gmail' ? 'Gmail' : 'SMTP'}
                      </Badge>
                      {account.is_default && (
                        <Badge variant="outline" className="text-xs border-blue-300 text-blue-700 bg-blue-50">
                          <Star className="w-3 h-3 mr-1" />
                          Predefinito
                        </Badge>
                      )}
                      {account.imap_host && (
                        <Badge variant="outline" className="text-xs border-green-300 text-green-700 bg-green-50">
                          <Inbox className="w-3 h-3 mr-1" />
                          {account.imap_protocol === 'pop3' ? 'POP3' : 'IMAP'}
                        </Badge>
                      )}
                      <Badge
                        variant="outline"
                        role={canExpandSig ? 'button' : undefined}
                        tabIndex={canExpandSig ? 0 : undefined}
                        aria-expanded={canExpandSig ? sigExpanded : undefined}
                        title={canExpandSig ? (sigExpanded ? 'Nascondi anteprima firma' : 'Mostra anteprima firma') : 'Nessuna firma configurata'}
                        className={`text-xs gap-1 ${sigBadgeClasses} ${canExpandSig ? 'cursor-pointer' : 'cursor-default'}`}
                        onClick={canExpandSig ? () => toggleSignatureExpanded(account.id) : undefined}
                        onKeyDown={canExpandSig ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggleSignatureExpanded(account.id);
                          }
                        } : undefined}
                      >
                        <PenLine className="w-3 h-3" />
                        {sigLabel}
                        {canExpandSig && (
                          sigExpanded
                            ? <ChevronUp className="w-3 h-3" />
                            : <ChevronDown className="w-3 h-3" />
                        )}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{account.email_address}</p>
                    <div className="flex items-center gap-3 mt-0.5">
                      {account.account_type === 'smtp' && account.smtp_host && (
                        <span className="text-xs text-muted-foreground">SMTP: {account.smtp_host}:{account.smtp_port}</span>
                      )}
                      {account.imap_host && (
                        <span className="text-xs text-muted-foreground">{account.imap_protocol === 'pop3' ? 'POP3' : 'IMAP'}: {account.imap_host}:{account.imap_port}</span>
                      )}
                      <span className="text-xs text-muted-foreground">
                        {account.account_type === 'gmail'
                          ? (account.gmail_shared
                              ? `Condiviso${(account.assignment_count || 0) > 1 ? ` con ${(account.assignment_count || 1) - 1} altr${(account.assignment_count || 1) - 1 === 1 ? 'o' : 'i'} utent${(account.assignment_count || 1) - 1 === 1 ? 'e' : 'i'}` : ' (solo proprietario)'}`
                              : 'Privato (solo proprietario Gmail)')
                          : ((account.assignment_count || 0) === 0
                              ? 'Disponibile a tutti'
                              : `Assegnato a ${account.assignment_count} utent${account.assignment_count === 1 ? 'e' : 'i'}`)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-1">
                  {account.last_test_result && (
                    <div className="flex items-center gap-1 mr-2">
                      {account.last_test_result === 'success' ? (
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                      ) : (
                        <XCircle className="w-4 h-4 text-red-500" />
                      )}
                      <span className={`text-xs ${account.last_test_result === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                        {account.last_test_result === 'success' ? 'Connesso' : 'Errore'}
                      </span>
                    </div>
                  )}

                  {account.imap_host && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSyncInbox(account.id)}
                      disabled={syncingId === account.id}
                      title="Sincronizza inbox"
                    >
                      {syncingId === account.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 text-green-500" />}
                    </Button>
                  )}

                  {account.account_type === 'gmail' && (
                    <div
                      className="flex items-center gap-1 mr-1 px-2 py-1 rounded border bg-muted"
                      title={account.gmail_shared
                        ? 'Condivisione attiva: puoi assegnare l\'account ad altri utenti'
                        : 'Condivisione disattivata: solo il proprietario può usare questo account'}
                    >
                      <Switch
                        checked={!!account.gmail_shared}
                        disabled={togglingShare === account.id}
                        onCheckedChange={() => handleToggleGmailShared(account)}
                      />
                      <span className="text-xs text-muted-foreground">Condividi</span>
                    </div>
                  )}

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openAssignments(account.id)}
                    disabled={account.account_type === 'gmail' && !account.gmail_shared}
                    title={account.account_type === 'gmail' && !account.gmail_shared
                      ? 'Attiva la condivisione per assegnare utenti'
                      : 'Gestisci assegnazioni utenti'}
                  >
                    <Users className="w-4 h-4 text-purple-500" />
                  </Button>

                  {!account.is_default && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSetDefault(account.id)}
                      disabled={settingDefault === account.id}
                      title="Imposta come predefinito"
                    >
                      {settingDefault === account.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <StarOff className="w-4 h-4 text-muted-foreground" />}
                    </Button>
                  )}

                  {account.account_type === 'gmail' && account.google_account_id && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openGmailSignature(account)}
                      title="Modifica firma"
                    >
                      <PenLine className="w-4 h-4 text-amber-500" />
                    </Button>
                  )}

                  {account.account_type === 'smtp' && (
                    <>
                      <Button variant="ghost" size="sm" onClick={() => openEditForm(account)} title="Modifica">
                        <Edit className="w-4 h-4 text-muted-foreground" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(account.id)}
                        disabled={deletingId === account.id}
                        title="Elimina"
                      >
                        {deletingId === account.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4 text-red-400" />}
                      </Button>
                    </>
                  )}
                </div>
                </div>

                {canExpandSig && sigExpanded && (
                  <div className="mt-3 ml-14 border rounded-md bg-muted p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        Anteprima firma
                      </span>
                      {sigStatus === 'disabled' && (
                        <span className="text-xs text-amber-600">
                          Inserimento automatico disattivato
                        </span>
                      )}
                    </div>
                    <div
                      className="text-sm prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: sanitizeSignatureHtml(account.signature_html) }}
                    />
                  </div>
                )}
              </CardContent>
            </Card>
            );
          })}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={(open) => { if (!open) { setShowForm(false); setEditingId(null); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Modifica Account Email' : 'Nuovo Account Email'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nome Visualizzato</Label>
                <Input
                  value={formData.display_name}
                  onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                  placeholder="es. Ufficio Commerciale"
                />
              </div>
              <div>
                <Label>Indirizzo Email *</Label>
                <Input
                  type="email"
                  value={formData.email_address}
                  onChange={(e) => setFormData({ ...formData, email_address: e.target.value })}
                  placeholder="es. info@azienda.it"
                />
              </div>
            </div>

            <div className="border rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-foreground flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Configurazione SMTP (Invio)
                </h3>
                <Button variant="outline" size="sm" onClick={handleTestSmtp} disabled={testingSmtp} className="gap-2">
                  {testingSmtp ? <Loader2 className="w-3 h-3 animate-spin" /> : <TestTube className="w-3 h-3" />}
                  Test SMTP
                </Button>
              </div>

              {smtpTestResult && (
                <div className={`p-3 rounded-lg text-sm flex items-center gap-2 ${smtpTestResult.success ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                  {smtpTestResult.success ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                  {smtpTestResult.success ? 'Connessione SMTP riuscita!' : `Errore: ${smtpTestResult.error}`}
                </div>
              )}

              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2">
                  <Label>Host SMTP *</Label>
                  <Input
                    value={formData.smtp_host}
                    onChange={(e) => setFormData({ ...formData, smtp_host: e.target.value })}
                    placeholder="es. mail.azienda.it"
                  />
                </div>
                <div>
                  <Label>Porta</Label>
                  <Input
                    type="number"
                    value={formData.smtp_port}
                    onChange={(e) => setFormData({ ...formData, smtp_port: parseInt(e.target.value) || 587 })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Utente SMTP *</Label>
                  <Input
                    value={formData.smtp_user}
                    onChange={(e) => setFormData({ ...formData, smtp_user: e.target.value })}
                    placeholder="es. info@azienda.it"
                  />
                </div>
                <div>
                  <Label>Password SMTP {editingId ? '(lascia vuoto per non cambiare)' : '*'}</Label>
                  <Input
                    type="password"
                    value={formData.smtp_password}
                    onChange={(e) => setFormData({ ...formData, smtp_password: e.target.value })}
                    placeholder={editingId ? '••••••••' : 'Password SMTP'}
                  />
                </div>
              </div>

              <div>
                <Label>Sicurezza</Label>
                <Select value={formData.smtp_security} onValueChange={(v) => setFormData({ ...formData, smtp_security: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tls">TLS (porta 587)</SelectItem>
                    <SelectItem value="ssl">SSL (porta 465)</SelectItem>
                    <SelectItem value="starttls">STARTTLS</SelectItem>
                    <SelectItem value="none">Nessuna</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="border rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-foreground flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Configurazione Ricezione Email - Opzionale
                </h3>
                {formData.imap_host && (
                  <Button variant="outline" size="sm" onClick={handleTestImap} disabled={testingImap} className="gap-2">
                    {testingImap ? <Loader2 className="w-3 h-3 animate-spin" /> : <TestTube className="w-3 h-3" />}
                    Test {formData.imap_protocol === 'pop3' ? 'POP3' : 'IMAP'}
                  </Button>
                )}
              </div>

              {imapTestResult && (
                <div className={`p-3 rounded-lg text-sm flex items-center gap-2 ${imapTestResult.success ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                  {imapTestResult.success ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                  {imapTestResult.success ? `Connessione ${formData.imap_protocol === 'pop3' ? 'POP3' : 'IMAP'} riuscita!` : `Errore: ${imapTestResult.error}`}
                </div>
              )}

              <div>
                <Label>Protocollo di ricezione</Label>
                <Select
                  value={formData.imap_protocol}
                  onValueChange={(v) => {
                    const defaults = v === 'pop3'
                      ? { imap_protocol: v, imap_port: 995, imap_security: 'ssl' }
                      : { imap_protocol: v, imap_port: 993, imap_security: 'ssl' };
                    setFormData({ ...formData, ...defaults });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="imap">IMAP - Sincronizza e mantieni le email sul server</SelectItem>
                    <SelectItem value="pop3">POP3 - Scarica le email dal server</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  {formData.imap_protocol === 'pop3'
                    ? 'POP3 scarica le email e le rimuove dal server. Ideale per un solo dispositivo.'
                    : 'IMAP sincronizza le email mantenendole sul server. Consigliato per più dispositivi.'}
                </p>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2">
                  <Label>Host {formData.imap_protocol === 'pop3' ? 'POP3' : 'IMAP'}</Label>
                  <Input
                    value={formData.imap_host}
                    onChange={(e) => setFormData({ ...formData, imap_host: e.target.value })}
                    placeholder={formData.imap_protocol === 'pop3' ? 'es. pop.azienda.it' : 'es. imap.azienda.it'}
                  />
                </div>
                <div>
                  <Label>Porta</Label>
                  <Input
                    type="number"
                    value={formData.imap_port}
                    onChange={(e) => setFormData({ ...formData, imap_port: parseInt(e.target.value) || (formData.imap_protocol === 'pop3' ? 995 : 993) })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Utente</Label>
                  <Input
                    value={formData.imap_user}
                    onChange={(e) => setFormData({ ...formData, imap_user: e.target.value })}
                    placeholder="es. info@azienda.it"
                  />
                </div>
                <div>
                  <Label>Password {editingId ? '(lascia vuoto per non cambiare)' : ''}</Label>
                  <Input
                    type="password"
                    value={formData.imap_password}
                    onChange={(e) => setFormData({ ...formData, imap_password: e.target.value })}
                    placeholder={editingId ? '••••••••' : `Password ${formData.imap_protocol === 'pop3' ? 'POP3' : 'IMAP'}`}
                  />
                </div>
              </div>

              <div>
                <Label>Sicurezza connessione</Label>
                <Select value={formData.imap_security} onValueChange={(v) => setFormData({ ...formData, imap_security: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ssl">SSL/TLS (porta {formData.imap_protocol === 'pop3' ? '995' : '993'})</SelectItem>
                    <SelectItem value="starttls">STARTTLS (porta {formData.imap_protocol === 'pop3' ? '110' : '143'})</SelectItem>
                    <SelectItem value="none">Nessuna (non consigliato)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="border rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-foreground flex items-center gap-2">
                  <PenLine className="w-4 h-4" />
                  Firma Email
                </h3>
              </div>

              <div className="flex items-center gap-3">
                <Switch
                  checked={formData.signature_enabled}
                  onCheckedChange={(checked) => setFormData({ ...formData, signature_enabled: checked })}
                />
                <Label>Aggiungi firma automaticamente alle nuove email</Label>
              </div>

              <div className="flex items-center gap-3">
                <Switch
                  checked={formData.signature_on_reply}
                  onCheckedChange={(checked) => setFormData({ ...formData, signature_on_reply: checked })}
                />
                <Label>Includi la firma anche nelle risposte e negli inoltri</Label>
              </div>

              <div>
                <Label>Contenuto firma</Label>
                <RichTextEditor
                  value={formData.signature_html}
                  onChange={(value) => setFormData({ ...formData, signature_html: value })}
                  placeholder="Es. Mario Rossi&#10;Direttore Commerciale&#10;Azienda S.r.l."
                  minHeight={140}
                  onImageUpload={uploadSignatureImage}
                  onImageError={handleImageUploadError}
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Inserisci immagini (logo, foto) tramite il pulsante immagine, drag&amp;drop o incolla. Formati supportati: PNG, JPEG, WebP. Max 1 MB.
                </p>
              </div>

              {formData.signature_html && (
                <div>
                  <Label className="text-xs text-muted-foreground">Anteprima</Label>
                  <div
                    className="mt-1 p-3 border rounded-md bg-muted text-sm prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: sanitizeSignatureHtml(formData.signature_html) }}
                  />
                </div>
              )}
            </div>

            <div className="flex items-center gap-3 px-1">
              <Switch
                checked={formData.is_default}
                onCheckedChange={(checked) => setFormData({ ...formData, is_default: checked })}
              />
              <Label>Imposta come account predefinito</Label>
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => { setShowForm(false); setEditingId(null); }}>Annulla</Button>
            <Button onClick={handleSave} disabled={saving} className="gap-2">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {editingId ? 'Aggiorna' : 'Crea Account'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showAssignments} onOpenChange={(open) => { if (!open) setShowAssignments(false); }}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Assegnazione Utenti
            </DialogTitle>
            {assignAccount && (
              <p className="text-sm text-muted-foreground mt-1">
                {assignAccount.display_name} ({assignAccount.email_address})
              </p>
            )}
          </DialogHeader>

          <div className="space-y-3">
            {tenantUsers.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground mr-2" />
                <span className="text-muted-foreground">Caricamento utenti...</span>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-3 p-2 rounded-md bg-muted border">
                  <Switch
                    checked={assignGlobal}
                    onCheckedChange={(v) => setAssignGlobal(v)}
                  />
                  <div className="text-sm">
                    <div className="font-medium">Disponibile a tutti</div>
                    <div className="text-xs text-muted-foreground">
                      {assignGlobal
                        ? 'Ogni utente del tenant può usare questo account.'
                        : 'Solo gli utenti selezionati qui sotto possono usare questo account.'}
                    </div>
                  </div>
                </div>
                <div className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 ${assignGlobal ? 'opacity-50 pointer-events-none' : ''}`}>
                  {tenantUsers.map((user) => {
                    const isOwner = assignAccount?.account_type === 'gmail'
                      && !!assignAccount?.gmail_owner_id
                      && assignAccount.gmail_owner_id === user.id;
                    const isSelected = assignedUserIds.includes(user.id) || isOwner;
                    return (
                      <button
                        key={user.id}
                        type="button"
                        onClick={() => { if (!isOwner) toggleUserAssignment(user.id); }}
                        disabled={assignGlobal || isOwner}
                        className={`flex items-center gap-2 p-2 rounded-md border text-sm transition-colors text-left ${
                          isSelected
                            ? 'bg-blue-50 border-blue-300 text-blue-800'
                            : 'bg-card border-border text-muted-foreground hover:bg-accent'
                        } ${isOwner ? 'cursor-not-allowed' : ''}`}
                      >
                        <div className={`h-4 w-4 rounded border flex items-center justify-center shrink-0 ${
                          isSelected ? 'bg-blue-600 border-blue-600' : 'border-border'
                        }`}>
                          {isSelected && <CheckCircle className="h-3 w-3 text-white" />}
                        </div>
                        <div className="truncate">
                          <div className="font-medium truncate">
                            {user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.username || user.email}
                            {isOwner && <span className="ml-1 text-xs text-blue-700">(proprietario Gmail)</span>}
                          </div>
                          <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowAssignments(false)}>Annulla</Button>
            <Button onClick={handleSaveAssignments} disabled={savingAssignments} className="gap-2">
              {savingAssignments && <Loader2 className="w-4 h-4 animate-spin" />}
              Salva Assegnazioni
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showGmailSig} onOpenChange={(open) => { if (!open) { setShowGmailSig(false); setGmailSigAccount(null); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PenLine className="w-5 h-5" />
              Firma Email Gmail
            </DialogTitle>
            {gmailSigAccount && (
              <p className="text-sm text-muted-foreground mt-1">
                {gmailSigAccount.display_name} ({gmailSigAccount.email_address})
              </p>
            )}
          </DialogHeader>

          {gmailSigLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Switch
                  checked={gmailSigForm.signature_enabled}
                  onCheckedChange={(checked) => setGmailSigForm({ ...gmailSigForm, signature_enabled: checked })}
                />
                <Label>Aggiungi firma automaticamente alle nuove email</Label>
              </div>

              <div className="flex items-center gap-3">
                <Switch
                  checked={gmailSigForm.signature_on_reply}
                  onCheckedChange={(checked) => setGmailSigForm({ ...gmailSigForm, signature_on_reply: checked })}
                />
                <Label>Includi la firma anche nelle risposte e negli inoltri</Label>
              </div>

              <div>
                <Label>Contenuto firma</Label>
                <RichTextEditor
                  value={gmailSigForm.signature_html}
                  onChange={(value) => setGmailSigForm({ ...gmailSigForm, signature_html: value })}
                  placeholder="Es. Mario Rossi&#10;Direttore Commerciale&#10;Azienda S.r.l."
                  minHeight={160}
                  onImageUpload={uploadSignatureImage}
                  onImageError={handleImageUploadError}
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Inserisci immagini (logo, foto) tramite il pulsante immagine, drag&amp;drop o incolla. Formati supportati: PNG, JPEG, WebP. Max 1 MB.
                </p>
              </div>

              {gmailSigForm.signature_html && (
                <div>
                  <Label className="text-xs text-muted-foreground">Anteprima</Label>
                  <div
                    className="mt-1 p-3 border rounded-md bg-muted text-sm prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: sanitizeSignatureHtml(gmailSigForm.signature_html) }}
                  />
                </div>
              )}

              <div className="text-xs text-muted-foreground">
                La firma è gestita da questo CRM e viene inserita nell'editor al momento della scrittura. La firma configurata su Gmail non viene modificata.
              </div>
            </div>
          )}

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => { setShowGmailSig(false); setGmailSigAccount(null); }}>Annulla</Button>
            <Button onClick={handleSaveGmailSignature} disabled={gmailSigSaving || gmailSigLoading} className="gap-2">
              {gmailSigSaving && <Loader2 className="w-4 h-4 animate-spin" />}
              Salva Firma
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-700">
            <p className="font-medium mb-1">Come funziona</p>
            <ul className="list-disc pl-4 space-y-1 text-blue-600">
              <li>Gli account <strong>Gmail</strong> vengono importati automaticamente dalle Integrazioni Google configurate</li>
              <li>Gli account <strong>SMTP</strong> possono essere aggiunti manualmente con le credenziali del server di posta</li>
              <li>Configura <strong>IMAP</strong> per sincronizzare la posta in arrivo e visualizzarla nella Messaggistica</li>
              <li>Usa le <strong>Assegnazioni</strong> per controllare quali utenti possono usare ogni account</li>
              <li>L'account <strong>predefinito</strong> viene usato automaticamente quando non ne viene selezionato uno specifico</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
